import React, { useMemo, useState } from 'react';
import { Box, useResponsive } from '@common-fe/common-fe';

import { Roles } from '@/common/constants';
import { MobilePageModalWrapper } from '@/components';
import { useHasAccess } from '@/modules/core/hooks';
import { useMyEnrollments, useOrganizationProperties } from '@/modules/transaction/hooks';
import { ManageProfileTabs } from '@/modules/user/components/ManageProfile/manageProfile.types';
import { PaymentMethodType } from '@/modules/user/user.types';

import Beneficiaries from './components/Beneficiaries/Beneficiaries';
import CommunicationPreferences from './components/CommunicationPreferences/CommunicationPreferences';
import Dependents from './components/Dependents/Dependents';
import ManageProfileDashboard from './components/ManageProfileDashboard/ManageProfileDashboard';
import ManageProfileModals from './components/ManageProfileModals/ManageProfileModals';
import useSetPaymentMethodsInStore from './components/ReimbursementOptions/hooks/useSetPaymentMethodsInStore';
import ReimbursementOptions from './components/ReimbursementOptions/ReimbursementOptions';
import Security from './components/Security/Security';
import HelpMobileScreen from './HelpScreen/HelpScreen';
import { ManageProfileTabsToggle } from './components';

const ManageProfile = () => {
  const [isMobileModal, setIsMobileModal] = useState(false);
  const [tab, setTab] = useState<string>('');
  const { isMobile } = useResponsive();
  const { isHSAExists: hasBeneficiariesModule, isLoading } = useMyEnrollments();

  const isLimitedAccess = useHasAccess([{ role: Roles.employeeLimitedAccess }]);

  const { organizationProperties } = useOrganizationProperties();
  const {
    refetch: refetchPaymentMethods,
    isFetching,
    formattedData: paymentMethods,
  } = useSetPaymentMethodsInStore();

  const isAlreadyHasCheckPaymentMethod = useMemo(
    () => !!paymentMethods?.find((paymentMethod) => paymentMethod?.type === PaymentMethodType.CHECK),
    [paymentMethods]
  );

  const hasAllowedPaymentMethods = useMemo(() => {
    const allowedPaymentMethods = organizationProperties?.allowedPaymentMethods;

    if (
      allowedPaymentMethods?.length === 1 &&
      allowedPaymentMethods?.[0] === PaymentMethodType.CHECK &&
      isAlreadyHasCheckPaymentMethod
    ) {
      return false;
    }

    return !!allowedPaymentMethods?.length;
  }, [organizationProperties, isAlreadyHasCheckPaymentMethod]);

  return (
    <>
      <Box
        height={isMobile ? '100vh' : {}}
        margin={isMobile ? { bottom: 'spacing24', top: 'spacing12' } : { bottom: 'spacing24' }}
      >
        <ManageProfileDashboard
          isLimitedAccess={isLimitedAccess}
          refetchPaymentMethods={refetchPaymentMethods}
          hasAllowedPaymentMethods={hasAllowedPaymentMethods}
        />

        <ManageProfileTabsToggle
          tab={tab}
          onSetTab={setTab}
          isLoading={isLoading}
          onSetMobileModal={setIsMobileModal}
          hasBeneficiariesModule={hasBeneficiariesModule}
        />
        <Box margin={{ top: 'spacing24' }}>
          {tab === ManageProfileTabs.COMMUNICATION_PREFERENCES && (
            <MobilePageModalWrapper
              title="Communication Preferences"
              isVisible={isMobileModal}
              onSetVisible={setIsMobileModal}
            >
              <CommunicationPreferences />
            </MobilePageModalWrapper>
          )}
          {tab === ManageProfileTabs.REIMBURSEMENT_OPTIONS && (
            <MobilePageModalWrapper
              title="Reimbursement Options"
              isVisible={isMobileModal}
              onSetVisible={setIsMobileModal}
            >
              <ReimbursementOptions
                isFetching={isFetching}
                refetchPaymentMethods={refetchPaymentMethods}
                hasAllowedPaymentMethods={hasAllowedPaymentMethods}
              />
            </MobilePageModalWrapper>
          )}
          {tab === ManageProfileTabs.DEPENDENTS && (
            <MobilePageModalWrapper
              title="Dependents"
              isVisible={isMobileModal}
              onSetVisible={setIsMobileModal}
            >
              <Dependents />
            </MobilePageModalWrapper>
          )}
          {tab === ManageProfileTabs.SECURITY && (
            <MobilePageModalWrapper
              title="Security"
              isVisible={isMobileModal}
              onSetVisible={setIsMobileModal}
            >
              <Security />
            </MobilePageModalWrapper>
          )}
          {tab === ManageProfileTabs.BENEFICIARIES && hasBeneficiariesModule ? (
            <MobilePageModalWrapper
              title="Beneficiaries"
              isVisible={isMobileModal}
              onSetVisible={setIsMobileModal}
            >
              <Beneficiaries />
            </MobilePageModalWrapper>
          ) : null}
          {tab === ManageProfileTabs.HELP ? (
            <MobilePageModalWrapper title="Help" isVisible={isMobileModal} onSetVisible={setIsMobileModal}>
              <HelpMobileScreen />
            </MobilePageModalWrapper>
          ) : null}
        </Box>
      </Box>
      <ManageProfileModals refetchPaymentMethods={refetchPaymentMethods} />
    </>
  );
};

export default ManageProfile;
